import { z } from 'zod';

/**
 * Staff in-app notifications — the bell in the ERP admin header.
 *
 * Rows are LEAN and locale-neutral: `eventType` + a small `payload` of facts
 * (doc number, customer name, amount + currency, source). The web client
 * renders the title/body through i18n so Arabic and English are both correct.
 * Read state is a per-user watermark server-side; each row arrives with a
 * computed `unread` flag for the caller.
 */

/** The facts a notification row may carry. All optional — render what exists. */
export interface StaffNotificationPayload {
  /** Human document number (SO-000123, INV-000042, POS sale number, payroll run …). */
  docNumber?: string;
  /** Customer/supplier display name, when the event has one. */
  partyName?: string;
  /** Money amount as a string (ADR-0005: no client arithmetic). */
  amount?: string;
  currencyCode?: string;
  /** Online-order origin: 'web' | 'app'. */
  source?: string;
  /** Checkout payment method id (cod, fib, …) for online orders. */
  paymentMethod?: string;
  /** invoice.posted: true when the document is a credit note. */
  isCreditNote?: boolean;
  /** coupon.offline_unverified: the code the customer quoted at the till. */
  couponCode?: string;
  /** coupon.offline_unverified: why it couldn't be verified (e.g. 'coupon_expired'). */
  couponReason?: string;
  /** coupon.offline_unverified: what the server now computes the coupon is worth. */
  serverAmount?: string;
  /** coupon.offline_unverified / store.order.counter: the cashier or rep who took the sale. */
  repName?: string;
  /** customer.type_requested: the customer type (price list) they asked for. */
  requestedTypeName?: string;
  /** customer.type_requested: how to reach them without opening the record. */
  partyPhone?: string;
}

export interface StaffNotification {
  id: string;
  eventType: string;
  entityId: string | null;
  /** In-app route to open when clicked (e.g. '/cms/orders/<id>'). */
  linkPath: string | null;
  payload: StaffNotificationPayload;
  /** True when created after the calling user's last-seen watermark. */
  unread: boolean;
  createdAt: string;
}

export interface StaffNotificationList {
  items: StaffNotification[];
  /** Unread rows among the event types this user may see (not just this page). */
  unreadCount: number;
}

export interface StaffNotificationUnreadCount {
  count: number;
}

export const StaffNotificationListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  offset: z.coerce.number().int().min(0).max(1000).default(0),
});
export type StaffNotificationListQuery = z.infer<typeof StaffNotificationListQuerySchema>;
